import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend
} from "recharts";
import { VulnerabilityTrendPoint } from "../../../utils/hooks/dashboard/useExecutiveSummaryData";
import { getVulnerabilityTrend } from "../../../utils/api/endpoints/dashboard/dashboardApi";
import { FiLoader } from "react-icons/fi";
import { MdInfo } from "react-icons/md";
import ChartFilter, { TimeRange, getPeriodLabel } from "./ChartFilter";

interface VulnerabilityTrendChartProps {
  data: VulnerabilityTrendPoint[];
  isSubscriptionOnly?: boolean;
  selectedYear?: number | null;
  subscriptionStartDate?: string | null;
}

const VulnerabilityTrendChart: React.FC<VulnerabilityTrendChartProps> = ({ 
  data: initialData, 
  isSubscriptionOnly = false, 
  selectedYear = null,
  subscriptionStartDate 
}) => {
  const [timeFilter, setTimeFilter] = useState<TimeRange>(
    (selectedYear !== null || isSubscriptionOnly) ? 'yearly' : 'all'
  );
  const [localData, setLocalData] = useState<VulnerabilityTrendPoint[]>(initialData);
  const [periodLabel, setPeriodLabel] = useState<string>('Yearly');
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialMount, setIsInitialMount] = useState(true);
  const [showInfo, setShowInfo] = useState(false);

  // Update local data if the parent data changes
  useEffect(() => {
    setLocalData(initialData);
  }, [initialData]);

  // Sync local timeFilter with global filters when they change
  useEffect(() => {
    if (selectedYear !== null || isSubscriptionOnly) {
      setTimeFilter('yearly');
    } else {
      setTimeFilter('all');
    }
  }, [selectedYear, isSubscriptionOnly]);

  const currentYear = new Date().getFullYear();
  const effectiveTimeFilter = (selectedYear && selectedYear !== currentYear) 
    ? 'yearly' 
    : timeFilter;

  // Fetch filtered data when timeFilter, isSubscriptionOnly or selectedYear changes
  useEffect(() => {
    if (isInitialMount) {
      setIsInitialMount(false);
      return;
    }

    let isMounted = true;
    const fetchFilteredData = async () => {
      setIsLoading(true);
      try {
        const response = await getVulnerabilityTrend(effectiveTimeFilter, isSubscriptionOnly, selectedYear);
        if (isMounted && response?.success && response.data) {
          const fetchedData = response.data.data || response.data;
          setLocalData(fetchedData);
          if (response.data.periodInfo?.label) {
            setPeriodLabel(response.data.periodInfo.label);
          }
        }
      } catch (e) {
        console.error("Failed to fetch filtered vulnerability trend", e);
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    fetchFilteredData();
    return () => { isMounted = false; };
  }, [effectiveTimeFilter, isSubscriptionOnly, selectedYear]);

  const displayFilterName = periodLabel;

  return (
    <motion.div
      className="bg-white rounded-xl shadow-sm border border-slate-100 flex flex-col h-full relative"
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
    >
      <div className="px-6 py-4 border-b border-gray-50 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex flex-col min-w-0 flex-1">
          <h3 className="text-sm font-semibold text-slate-800 break-words min-w-0">
            Vulnerability Trend
          </h3>
          <span className="text-[11px] text-slate-400 font-semibold mt-0.5">
            {(selectedYear && selectedYear !== currentYear) ? `Year ${selectedYear}` : getPeriodLabel(timeFilter, isSubscriptionOnly, subscriptionStartDate)}
          </span>
        </div>
        
        <div className="flex items-center gap-2">
          <div className="relative flex-shrink-0">
            <MdInfo 
              className="h-4 w-4 text-slate-300 hover:text-blue-500 cursor-help transition-colors"
              onMouseEnter={() => setShowInfo(true)}
              onMouseLeave={() => setShowInfo(false)}
            />
            <AnimatePresence>
              {showInfo && (
                <motion.div
                  initial={{ opacity: 0, y: 10, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 10, scale: 0.95 }}
                  className="absolute left-0 top-full mt-2 z-50 w-64 bg-[#010D2C] text-white p-3 rounded-xl text-[11px] font-medium shadow-xl pointer-events-none leading-relaxed"
                >
                  Compares the number of vulnerabilities reported against the number of vulnerabilities resolved. This helps you understand if your remediation team is keeping up with the discovery rate (includes accepts risk/non-actionable reports).
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {(!selectedYear || selectedYear === currentYear) && (
            <ChartFilter
              active={timeFilter}
              onChange={setTimeFilter}
              isSubscriptionOnly={isSubscriptionOnly}
              subscriptionStartDate={subscriptionStartDate}
              supportsAllTime={!selectedYear}
              selectedYear={selectedYear}
            />
          )}
        </div>
      </div>

      <div className="pt-6 px-6 pb-3 h-[395px] relative overflow-hidden flex flex-col">
        <AnimatePresence>
          {isLoading && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 z-10 flex items-center justify-center bg-white/80 backdrop-blur-sm rounded-b-xl"
            >
              <FiLoader className="w-8 h-8 text-blue-500 animate-spin" />
            </motion.div>
          )}
        </AnimatePresence>

        {localData && localData.length > 0 ? (
          <div className="w-full h-full overflow-x-auto custom-scrollbar">
            <div style={{ minWidth: localData.length > 6 ? `${localData.length * 70}px` : '100%', height: '100%' }}>
              <ResponsiveContainer width="100%" height="100%" debounce={50}>
                <LineChart data={localData} margin={{ top: 10, right: 50, left: 15, bottom: 40 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F1F5F9" />
                  <XAxis 
                    dataKey="month" 
                    axisLine={false} 
                    tickLine={false} 
                    tick={{ fontSize: 10, fontWeight: 700, fill: '#94A3B8' }}
                    dy={10}
                    interval={0}
                  />
                  <YAxis 
                    axisLine={false} 
                    tickLine={false} 
                    allowDecimals={false}
                    tick={{ fontSize: 10, fontWeight: 700, fill: '#94A3B8' }}
                    label={{ 
                      value: 'Number of Vulnerabilities', 
                      angle: -90, 
                      position: 'insideLeft', 
                      offset: -10, 
                      style: { fill: '#6B7280', fontSize: 10, fontWeight: 700, textAnchor: 'middle' } 
                    }}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      borderRadius: '16px', 
                      border: 'none', 
                      boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
                      fontSize: '11px',
                      fontWeight: '800'
                    }}
                  />
                  <Legend 
                    verticalAlign="bottom" 
                    align="center" 
                    iconType="circle"
                    wrapperStyle={{ fontSize: '10px', fontWeight: 700, bottom: '5px' }}
                  />
                  <Line
                    type="monotone"
                    dataKey="reported"
                    name="Reported"
                    stroke="#3B82F6"
                    strokeWidth={3}
                    strokeDasharray="5 5"
                    dot={{ r: 4, fill: "#3B82F6", strokeWidth: 2, stroke: "#FFF" }}
                    activeDot={{ r: 6 }}
                    animationDuration={400}
                  />
                  <Line
                    type="monotone"
                    dataKey="resolved"
                    name="Resolved"
                    stroke="#10B981"
                    strokeWidth={3}
                    dot={{ r: 4, fill: "#10B981", strokeWidth: 2, stroke: "#FFF" }}
                    activeDot={{ r: 6 }}
                    animationDuration={400}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        ) : (
          <div className="h-full w-full flex items-center justify-center">
            <p className="text-sm font-medium text-gray-400 italic">No trend data available for the selected timeframe.</p>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default React.memo(VulnerabilityTrendChart);


